import { useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * `FE-04.10` primitive — resolved in `docs/design/stage2/B3-BRIEF.md` §4.
 *
 * `tone` is REQUIRED, not optional, and that is the whole point of the
 * component. `entry-surfaces.md` (`FR-EXP-034`'s edge cases) and
 * `finding-things.md` (`FR-SEARCH-015` AC4) argued for it independently:
 * "nothing found" and "the request failed" must never be drawn the same way.
 * A default would silently make a failed load look like an empty one, so
 * there is deliberately no `defaultVariants` entry for it below — an omitted
 * `tone` is a compile error, not a silent `neutral`.
 *
 * `tone='error'` announces `role="alert"` (assertive, read as soon as it
 * mounts). `tone='neutral'` renders no live-region role at all: per
 * entry-surfaces D5, "nothing; the heading is the announcement" — a member
 * tabbing or navigating by heading encounters the title directly.
 *
 * ⚠ **This carries its own opaque sheet — do NOT wrap it in `CardSurface`.**
 * entry-surfaces' return specifies "renders on a `CardSurface variant='sheet'`
 * **always**", and B3 satisfied that requirement by applying the sheet here
 * rather than by composing the component: the binding rule is that the body is
 * `--color-ink-muted`, which B1's 92% panel floor forbids on anything
 * translucent, and one opaque sheet discharges it. A caller who reads that
 * sentence literally and wraps this gets a nested radius and a doubled shadow,
 * which looks like a styling bug rather than a composition one. Same for
 * `Notice`.
 *
 * Both size values fill a *sheet*, never a page: `'panel'` is the version
 * that fills a list panel, a workspace group or `<main>`; `'inline'` is the
 * version that sits inside a smaller row (a single list's "no cards match").
 *
 * Sits on an opaque `--color-surface-raised` — never `--color-panel` — because
 * its body is `--color-ink-muted`, and B1's measured floor (panel 92%, muted
 * text needs 98%) forbids muted text on anything translucent. All three
 * Stage 2 returns hit this independently.
 */
const emptyStateVariants = cva(
  'flex flex-col items-center justify-center gap-3 rounded-card bg-surface-raised text-center',
  {
    variants: {
      size: {
        inline: 'gap-2 p-6',
        panel: 'gap-3 p-12',
      },
    },
    defaultVariants: {
      size: 'inline',
    },
  },
);

export interface EmptyStateProps
  extends
    Omit<ComponentPropsWithoutRef<'div'>, 'title'>,
    VariantProps<typeof emptyStateVariants> {
  /**
   * REQUIRED. `'neutral'` — nothing here, no action was refused.
   * `'error'` — the request itself failed; renders `role="alert"` and is
   * where the caller's retry control (`action`) belongs.
   */
  tone: 'neutral' | 'error';
  /** Always visible; carries `role="heading"` so it is reachable by heading
   * navigation even though a primitive cannot know its place in the outline. */
  title: string;
  /**
   * The heading rank of `title`. Defaults to `2`, which is what this component
   * shipped with and is right for an empty state that *is* the surface.
   *
   * ⚠ **It exists because a primitive cannot know its place in the outline, and
   * `C1` found the case where guessing is wrong.** An empty *list* renders
   * `ListEmpty` inside a `ListColumn` whose own list name is already an `<h2>`
   * — so the default nests two rank-2 headings in one section, and a screen
   * reader user navigating by heading hears the list named twice at the same
   * level with no indication that the second is subordinate to the first.
   * Nothing about it is visible, no test in either file could see it (each was
   * correct alone), and it was found only by composing them.
   *
   * Pass the rank the *caller's* outline requires: `3` under an `<h2>`, `2`
   * under an `<h1>`.
   */
  headingLevel?: 2 | 3 | 4;
  body?: ReactNode;
  action?: ReactNode;
  /**
   * Not in the brief's fixed cross-file signature, but entry-surfaces asked
   * for it explicitly (its filter-empty and no-workspaces states both carry
   * a "widen your search" style secondary link next to the primary action)
   * and nothing in the other two returns contradicts it — union, not a name
   * collision. Renders after `action`.
   */
  secondaryAction?: ReactNode;
}

export function EmptyState({
  tone,
  size,
  title,
  headingLevel = 2,
  body,
  action,
  secondaryAction,
  className,
  ...rest
}: EmptyStateProps) {
  // role="alert" takes its accessible name from an author-supplied source
  // (`aria-label`/`aria-labelledby`), never from its rendered content — so
  // without this, the alert's accessible name would silently be empty even
  // though the title is right there on screen. `aria-labelledby` keeps the
  // heading text as the single source, rather than duplicating `title` into
  // a second `aria-label` string.
  const titleId = useId();

  const content = (
    <>
      <p
        id={titleId}
        role="heading"
        aria-level={headingLevel}
        className="font-display text-lg font-medium text-ink"
      >
        {title}
      </p>
      {body ? <div className="max-w-sm text-sm text-ink-muted">{body}</div> : null}
      {action || secondaryAction ? (
        <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </>
  );

  if (tone === 'error') {
    return (
      <div
        role="alert"
        aria-labelledby={titleId}
        className={cn(emptyStateVariants({ size }), className)}
        {...rest}
      >
        {content}
      </div>
    );
  }

  return (
    <div className={cn(emptyStateVariants({ size }), className)} {...rest}>
      {content}
    </div>
  );
}
